import { CouchUserCoachTierData, UserLicenseType } from '@cf-shared/data-model/couch-user';
import { formatDateMed } from '@cf-shared/date-format';
import { resolveSubscriptionEndInstants } from '@cf-shared/data-model/subscription-end-instants';

/**
 * The date a coach's QUEUED tier change actually takes effect, formatted for the
 * "your plan changes to N clients on <date>" notice.
 *
 * ## Which of the two dates this is
 *
 * A queued change is applied by the STORE at the coach's next renewal, so this
 * notice asks when the PROVIDER acts, not when our own access would end.
 *
 * **In practice only an Apple coach ever sees it**, and Apple stores a raw expiry
 * with no grace — so asking the right question changes nothing visible today.
 * Google Play applies a plan change at once rather than queueing it to the next
 * renewal, and never writes the `autoRenewProductId` the notice is gated on (see
 * `refreshQueuedDowngradeNoticeAsync` in coach-settings.ts, and the Apple-only
 * arm of `coach-tier-store-purchase.ts`). The Google Play branch below is
 * therefore DEFENSIVE, not a fix for a live defect: it is there so that if that
 * rail ever does queue a change, the date is right the first time instead of
 * being a day late. See `@cf-shared/data-model/subscription-end-instants` for
 * the rule it applies.
 *
 * ## Why it is out here rather than on the page
 *
 * So it can be unit-tested. The page class needs Angular to instantiate, and the
 * repository has no component test harness, so a rule left inside it is a rule
 * nothing checks.
 *
 * Returns undefined when no date can be shown — a missing block, an indefinite
 * (`'never'`) expiry, or an unparseable one. That is a real answer, not a
 * failure: the notice has separate wording for having no date (`bodyNoDate`), and
 * printing "Invalid DateTime" into the middle of a sentence a coach is meant to
 * act on would be worse than saying nothing.
 */
export function describeQueuedTierChangeDate(
  tier: CouchUserCoachTierData | undefined,
  locale: string,
): string | undefined {
  if (!tier) return undefined;
  const { providerActsAt, accessEndsAt } = resolveSubscriptionEndInstants(
    UserLicenseType.CoachTier,
    tier.expiresDate_utc,
    { coachTierData: tier },
  );
  // fall back to the access date if the store is somehow unknown, so a coach
  // keeps a date rather than losing the sentence entirely.
  const instant = providerActsAt ?? accessEndsAt;
  if (!instant) return undefined;
  // shown in the coach's OWN day: the field is stored UTC, so a coach west of UTC
  // whose renewal lands soon after midnight would otherwise be told the next date.
  return formatDateMed(instant.toLocal(), locale);
}
